/**
 * Helper de compatibilidade moment → date-fns
 *
 * Este arquivo fornece funções de compatibilidade para facilitar
 * a migração gradual de moment para date-fns.
 *
 * USO TEMPORÁRIO: Substitua pelos imports diretos de date-fns quando possível.
 *
 * @deprecated Use date-fns diretamente
 */
import {
  format,
  parse,
  parseISO,
  isValid,
  addDays,
  subDays,
  startOfDay,
  endOfDay,
  isBefore,
  isAfter,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
} from "date-fns";

/**
 * Cria um objeto similar ao moment para facilitar migração
 * @param {string|Date|number} input - Data de entrada
 * @param {string} formatStr - Formato de parse (opcional)
 */
export const moment = (input, formatStr) => {
  let date;

  if (!input) {
    date = new Date();
  } else if (input instanceof Date) {
    date = input;
  } else if (typeof input === "number") {
    date = new Date(input);
  } else if (typeof input === "string") {
    if (formatStr) {
      // Parse com formato específico
      const formatMap = {
        "DD/MM/YYYY": "dd/MM/yyyy",
        "DD-MM-YYYY": "dd-MM-yyyy",
        "YYYY-MM-DD": "yyyy-MM-dd",
        "YYYY/MM/DD": "yyyy/MM/dd",
        "DD/MM/YY": "dd/MM/yy",
        YYYY: "yyyy",
        "MM/DD/YYYY": "MM/dd/yyyy",
      };

      const dateFnsFormat = formatMap[formatStr] || formatStr;
      date = parse(input, dateFnsFormat, new Date());
    } else {
      // Tenta parse ISO
      date = parseISO(input);
    }
  } else {
    date = new Date();
  }

  // Retorna objeto com métodos compatíveis com moment
  return {
    _date: date,

    format: (formatStr) => {
      if (!isValid(date)) return "Invalid Date";

      // Mapeamento de formatos moment → date-fns
      const formatMap = {
        "DD/MM/YY": "dd/MM/yy",
        "DD/MM/YYYY": "dd/MM/yyyy",
        "DD-MM-YYYY": "dd-MM-yyyy",
        "YYYY-MM-DD": "yyyy-MM-dd",
        "YYYY/MM/DD": "yyyy/MM/dd",
        "DD/MM/YYYY HH:mm:ss": "dd/MM/yyyy HH:mm:ss",
        YYYY: "yyyy",
        "MM/DD/YYYY": "MM/dd/yyyy",
        "HH:mm": "HH:mm",
        MMMM: "MMMM",
      };

      const dateFnsFormat = formatMap[formatStr] || formatStr;

      try {
        return format(date, dateFnsFormat);
      } catch (error) {
        // Aviso deliberado: sinaliza formato não suportado pelo shim.
        // eslint-disable-next-line no-console
        console.warn(`Formato não suportado: ${formatStr}`, error);
        return format(date, "dd/MM/yyyy");
      }
    },

    isValid: () => isValid(date),

    add: (amount, unit) => {
      const newDate = addDays(date, unit === "days" ? amount : amount);
      return moment(newDate);
    },

    subtract: (amount, unit) => {
      const newDate = subDays(date, unit === "days" ? amount : amount);
      return moment(newDate);
    },

    startOf: (unit) => {
      if (unit === "day") return moment(startOfDay(date));
      return moment(date);
    },

    endOf: (unit) => {
      if (unit === "day") return moment(endOfDay(date));
      return moment(date);
    },

    isBefore: (otherDate) => {
      const other = otherDate._date || otherDate;
      return isBefore(date, other);
    },

    isAfter: (otherDate) => {
      const other = otherDate._date || otherDate;
      return isAfter(date, other);
    },

    // `granularity` existe só para manter a assinatura do moment; o shim ignora.
    // eslint-disable-next-line no-unused-vars
    isBetween: (startDate, endDate, granularity = null, inclusivity = "()") => {
      const start = startDate._date || startDate;
      const end = endDate._date || endDate;

      // Verifica inclusividade: '()' = exclusivo, '[]' = inclusivo, '[)' = inicio inclusivo, fim exclusivo, etc
      const includeStart = inclusivity.charAt(0) === "[";
      const includeEnd = inclusivity.charAt(1) === "]";

      const afterStart = includeStart
        ? !isBefore(date, start)
        : isAfter(date, start);
      const beforeEnd = includeEnd ? !isAfter(date, end) : isBefore(date, end);

      return afterStart && beforeEnd;
    },

    diff: (otherDate, unit = "milliseconds") => {
      const other = otherDate._date || otherDate;

      switch (unit) {
        case "days":
          return differenceInDays(date, other);
        case "hours":
          return differenceInHours(date, other);
        case "minutes":
          return differenceInMinutes(date, other);
        default:
          return date.getTime() - new Date(other).getTime();
      }
    },

    toDate: () => date,

    valueOf: () => date.getTime(),
  };
};

// Constantes moment
moment.ISO_8601 = "ISO_8601";

// Método estático para verificar validade
moment.isValid = (value) => {
  try {
    const date = new Date(value);
    return isValid(date);
  } catch {
    return false;
  }
};

export default moment;
